import {
  action,
  atom,
  onConnect,
  combine,
  reaction,
  withConcurrency,
  sleep,
} from '@reatom/framework'

import { Array, Result } from '@blade/std-lib'
import { followsRevisionAtom } from '@root/entities/activity-action-type'
import { FollowerDetail } from '@root/entities/follower-detail'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { reatomRequest } from '@root/shared/lib'

import type { DelugeId } from '@root/entities/user-short'
import type { Error } from '@root/shared'

type FilterStateArgs = {
  searchQuery: string
  delugeId: DelugeId
}

export const fetchFollowingFilterItemsRequestAction = action(
  async (
    ctx,
    { searchQuery, delugeId }: FilterStateArgs,
  ): Promise<
    Result.Result<
      ReadonlyArray<FollowerDetail.FollowerDetail>,
      Error.UnknownError
    >
  > => {
    const {
      tradeApiClient,
      environment: { assets },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.activityFollowersDetail, [
        {
          delugeId,
          followersType: 'following',
          name: searchQuery,
        },
      ]),
    )

    return result.pipe(
      Result.map(
        Array.map((x) => FollowerDetail.fromDto(x, assets.avatarsHost)),
      ),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'fetchFollowingFilterItemsRequestAction',
)

export const reatomFollowingsFilter = (delugeId: DelugeId) => {
  const searchQueryAtom = atom('', 'searchQueryAtom')

  const filterStateAtom = combine(
    { searchQuery: searchQueryAtom },
    'filterStateAtom',
  )

  const filterItemsStateAtom = reatomRequest({
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'filterItemsState',
    execAction: fetchFollowingFilterItemsRequestAction,
  })

  const filterStateChangeReaction = reaction(async (ctx) => {
    const filterState = ctx.spy(filterStateAtom)
    // Read again after a follow or an unfollow: the list is a server answer
    // and the button has just changed what it would be.
    ctx.spy(followsRevisionAtom)

    await ctx.schedule(() => sleep(300))

    filterItemsStateAtom.execute(ctx, {
      searchQuery: filterState.searchQuery,
      delugeId,
    })
  }, 'filterStateChangeReaction').pipe(withConcurrency())

  onConnect(filterItemsStateAtom, (ctx) => {
    const filterStateReactionResult = filterStateChangeReaction(ctx)

    return () => {
      filterStateReactionResult.unsubscribe()
    }
  })

  return {
    filterItemsStateAtom,
    searchQueryAtom,
  }
}

export type FollowingsFilter = ReturnType<typeof reatomFollowingsFilter>
